import { cn } from '@/utils/classUtils';
import { useCallback, useEffect, useRef, useState } from 'react';

export const CustomScrollbar = ({
  tableScrollRef,
}: {
  tableScrollRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const [scrollState, setScrollState] = useState({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });
  const isDragging = useRef(false);
  const trackRef = useRef<HTMLDivElement>(null);

  // Update scroll state when table scrolls or content changes
  useEffect(() => {
    const tableEl = tableScrollRef.current;
    if (!tableEl) return;

    const updateScrollState = () => {
      setScrollState({
        scrollLeft: tableEl.scrollLeft,
        scrollWidth: tableEl.scrollWidth,
        clientWidth: tableEl.clientWidth,
      });
    };

    updateScrollState();
    tableEl.addEventListener('scroll', updateScrollState);

    // Observe container and table content for size changes
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(tableEl);
    const tableContent = tableEl.querySelector('table');
    if (tableContent) {
      resizeObserver.observe(tableContent);
    }

    return () => {
      tableEl.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [tableScrollRef]);

  const { scrollLeft, scrollWidth, clientWidth } = scrollState;
  const hasOverflow = scrollWidth > clientWidth;

  const thumbWidth = hasOverflow
    ? Math.max((clientWidth / scrollWidth) * 100, 10)
    : 100;
  const thumbPosition = hasOverflow
    ? (scrollLeft / (scrollWidth - clientWidth)) * (100 - thumbWidth)
    : 0;

  const handleTrackClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!trackRef.current || !tableScrollRef.current || !hasOverflow) return;

      const trackRect = trackRef.current.getBoundingClientRect();
      const clickPosition = (e.clientX - trackRect.left) / trackRect.width;
      const maxScroll = scrollWidth - clientWidth;
      tableScrollRef.current.scrollLeft = clickPosition * maxScroll;
    },
    [tableScrollRef, hasOverflow, scrollWidth, clientWidth]
  );

  const handleThumbMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!hasOverflow) return;

      isDragging.current = true;
      const startX = e.clientX;
      const startScrollLeft = tableScrollRef.current?.scrollLeft ?? 0;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDragging.current || !trackRef.current || !tableScrollRef.current)
          return;

        const trackWidth = trackRef.current.getBoundingClientRect().width;
        const deltaX = moveEvent.clientX - startX;
        const scrollRatio = deltaX / (trackWidth * (1 - thumbWidth / 100));
        const maxScroll = scrollWidth - clientWidth;
        tableScrollRef.current.scrollLeft =
          startScrollLeft + scrollRatio * maxScroll;
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [tableScrollRef, hasOverflow, thumbWidth, scrollWidth, clientWidth]
  );

  if (!hasOverflow) return null;

  return (
    <div
      ref={trackRef}
      className={cn(
        'h-2.5 bg-neutral-100 rounded-full relative mx-1',
        hasOverflow && 'cursor-pointer'
      )}
      onClick={handleTrackClick}
    >
      {hasOverflow && (
        <div
          className="absolute top-0.5 bottom-0.5 bg-neutral-400 rounded-full cursor-grab active:cursor-grabbing hover:bg-neutral-500 transition-colors"
          style={{
            left: `${thumbPosition}%`,
            width: `${thumbWidth}%`,
          }}
          onMouseDown={handleThumbMouseDown}
          onClick={e => e.stopPropagation()}
        />
      )}
    </div>
  );
};
